import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";
import { ArrowRight, Calculator, ChatCircleText, Buildings, Clock, Coins } from "@phosphor-icons/react";
import { useCountry } from "@/lib/country";
import { PricingNote } from "@/components/PricingDisclaimer";
import MalaysiaPrograms from "@/components/MalaysiaPrograms";
import CanadaPrograms from "@/components/CanadaPrograms";

const CATEGORY_LABELS = {
  business_setup: "Business Setup",
  business_presence: "Business Presence",
  business_growth: "Business Growth",
  investment_expansion: "Investment & Expansion",
  professional_referrals: "Professional Referrals",
};

// Resolves the country slug from either /countries/:slug or /open-company-in-*
function useCountrySlug() {
  const params = useParams();
  const location = useLocation();
  if (params.slug) return params.slug;
  // SEO alias: /open-company-in-<slug>  →  extract everything after the prefix
  const m = location.pathname.match(/^\/open-company-in-(.+)$/);
  return m ? m[1] : "";
}

export default function CountryDetail() {
  const slug = useCountrySlug();
  const { setCountry } = useCountry();
  const [country, setCountry_] = useState(null);
  const [services, setServices] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);

  useEffect(() => {
    if (!slug) return;
    setCountry(slug);
    api.get(`/countries/${slug}`).then((r) => setCountry_(r.data));
    api.get(`/services?country=${slug}`).then((r) => setServices(r.data));
    api.get(`/case-studies?country=${capitalize(slug)}`).then((r) => setCaseStudies(r.data));
  }, [slug]);

  if (!country) return <div className="py-32 text-center text-gos-muted">Loading…</div>;

  const grouped = services.reduce((acc, s) => { (acc[s.category] = acc[s.category] || []).push(s); return acc; }, {});

  return (
    <div data-testid={`country-detail-${slug}`} className="fade-in">
      {/* Hero */}
      <section className="relative border-b border-gos-border overflow-hidden">
        {country.hero_image_url && (
          <div className="absolute inset-0 opacity-25">
            <img src={country.hero_image_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(248,246,240,0.4), rgba(248,246,240,0.95))" }} />
          </div>
        )}
        <div className="container-gos relative py-24">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-6xl">{country.flag}</span>
            <div>
              <div className="overline">Register company in</div>
              <h1 className="font-display text-5xl text-gos-charcoal tracking-tight">{country.name}</h1>
            </div>
          </div>
          <p className="text-lg text-gos-slate max-w-2xl leading-relaxed">{country.long_description}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-3xl">
            <Stat icon={Coins} label="Corp tax" value={`${country.corporate_tax_rate}%`} />
            <Stat icon={Clock} label="Setup time" value={`${country.setup_timeline_days} days`} />
            <Stat icon={Buildings} label="Foreign ownership" value={`${country.foreign_ownership_pct || 100}%`} />
            <Stat icon={ArrowRight} label="Rating" value={`${country.overall_rating}/10`} />
          </div>

          {(country.gov_fees_min_usd || country.gov_fees_max_usd) && (
            <div className="mt-8 max-w-3xl bg-white border border-gos-border rounded-sm p-5" data-testid="gov-fees-range">
              <div className="overline mb-2">Estimated setup cost</div>
              <div className="font-display text-2xl text-gos-charcoal">
                Starting from USD ${Number(country.gov_fees_min_usd || 0).toLocaleString()}
                {country.gov_fees_max_usd ? `–$${Number(country.gov_fees_max_usd).toLocaleString()}` : ""}
                <span className="text-sm text-gos-slate ml-2">government &amp; registration</span>
              </div>
              <div className="text-sm text-gos-slate mt-1">
                + USD $200 Open Business Anywhere service fee
              </div>
              {country.gov_fees_note && (
                <p className="text-xs text-gos-muted mt-3 italic">{country.gov_fees_note}</p>
              )}
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/calculator" className="btn-primary" data-testid="country-calc-btn"><Calculator size={16} weight="bold" /> Calculate cost</Link>
            <Link to="/contact" className="btn-sand" data-testid="country-consult-btn"><ChatCircleText size={16} weight="bold" /> Book consultation</Link>
            <Link to={`/compare?slugs=${slug}`} className="btn-secondary">Compare with another country</Link>
          </div>
        </div>
      </section>

      {/* Malaysia-only: MM2H + Labuan option cards */}
      {slug === "malaysia" && <MalaysiaPrograms />}

      {/* Canada-only: 3 immersion programme cards + Estimated Investment banner */}
      {slug === "canada" && <CanadaPrograms />}

      {/* Overview / Requirements / Business types (hidden for Malaysia — replaced by MM2H/Labuan cards above) */}
      {slug !== "malaysia" && (country.requirements_summary || (country.common_business_types || []).length > 0 || (country.expansion_opportunities || []).length > 0) && (
        <section className="py-16 bg-white border-b border-gos-border" data-testid="country-overview">
          <div className="container-gos grid grid-cols-1 lg:grid-cols-3 gap-8">
            {country.requirements_summary && (
              <div>
                <div className="overline mb-2">Requirements</div>
                <h3 className="font-display text-xl text-gos-charcoal mb-3">What you&apos;ll need</h3>
                <p className="text-sm text-gos-slate leading-relaxed">{country.requirements_summary}</p>
              </div>
            )}
            {(country.common_business_types || []).length > 0 && (
              <div>
                <div className="overline mb-2">Business structures</div>
                <h3 className="font-display text-xl text-gos-charcoal mb-3">Common types in {country.name}</h3>
                <ul className="space-y-1.5 text-sm text-gos-slate">
                  {country.common_business_types.map((t) => <li key={t} className="flex gap-2"><span className="text-gos-sand">•</span> {t}</li>)}
                </ul>
              </div>
            )}
            {(country.expansion_opportunities || []).length > 0 && (
              <div>
                <div className="overline mb-2">Expansion</div>
                <h3 className="font-display text-xl text-gos-charcoal mb-3">Opportunities to grow</h3>
                <ul className="space-y-1.5 text-sm text-gos-slate">
                  {country.expansion_opportunities.map((t) => <li key={t} className="flex gap-2"><span className="text-gos-sand">•</span> {t}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Services (hidden for Malaysia — MM2H + Labuan cards above replace the per-service grid) */}
      {slug !== "malaysia" && (
      <section className="py-20 bg-gos-bone">
        <div className="container-gos">
          <div className="overline mb-3">Services in {country.name}</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-gos-charcoal">Pricing in {country.currency_code}</h2>

          <PricingNote className="mt-6 max-w-3xl" />

          <div className="mt-12 space-y-12">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat} data-testid={`category-${cat}`}>
                <div className="overline text-gos-navy mb-4">{CATEGORY_LABELS[cat] || cat}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((s) => (
                    <div key={s.slug} className="bg-white border border-gos-border p-6 rounded-sm">
                      <div className="font-display text-lg text-gos-navy">{s.name}</div>
                      <p className="text-xs text-gos-slate mt-2 leading-relaxed">{s.description}</p>
                      <div className="mt-4 border-t border-gos-border pt-4 font-mono text-sm flex justify-between">
                        <div>
                          <div className="text-[10px] text-gos-muted uppercase tracking-widest">One-time</div>
                          <div className="text-gos-charcoal">{s.price?.one_time ? formatMoney(s.price.one_time, country.currency_code) : "—"}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] text-gos-muted uppercase tracking-widest">Monthly</div>
                          <div className="text-gos-charcoal">{s.price?.monthly ? formatMoney(s.price.monthly, country.currency_code) : "—"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Case studies */}
      {caseStudies.length > 0 && (
        <section className="py-20 bg-white border-t border-gos-border">
          <div className="container-gos">
            <div className="overline mb-3">Stories</div>
            <h2 className="font-display text-3xl tracking-tight text-gos-charcoal mb-10">Clients who built in {country.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {caseStudies.map((cs) => (
                <Link to={`/case-studies/${cs.slug}`} key={cs.id} className="card-lift border border-gos-border rounded-sm overflow-hidden">
                  {cs.image_url && <img src={cs.image_url} alt={cs.title} className="aspect-[16/10] object-cover w-full" />}
                  <div className="p-6">
                    <div className="overline text-gos-sand mb-2">{cs.client_origin} → {cs.target_country}</div>
                    <div className="font-display text-lg text-gos-navy">{cs.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {(country.faqs || []).length > 0 && (
        <section className="py-20 bg-gos-bone border-t border-gos-border" data-testid="country-faqs">
          <div className="container-gos max-w-3xl">
            <div className="overline mb-3">FAQs</div>
            <h2 className="font-display text-3xl text-gos-charcoal mb-8">Common questions about {country.name}</h2>
            <div className="space-y-3">
              {country.faqs.map((f, i) => (
                <details key={`faq-${i}-${(f.q || "").slice(0, 16)}`} className="bg-white border border-gos-border rounded-sm p-4 group">
                  <summary className="cursor-pointer font-medium text-gos-charcoal flex items-center justify-between">
                    <span>{f.q}</span>
                    <span className="text-gos-navy group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="text-sm text-gos-slate leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 border-t border-gos-border" data-testid="country-cta">
        <div className="container-gos text-center max-w-2xl mx-auto">
          <h3 className="font-display text-2xl text-gos-charcoal">Ready to open a company in {country.name}?</h3>
          <p className="text-gos-slate mt-2">Get a free assessment from our team — usually back to you within one business day.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/calculator" className="btn-primary">Calculate cost <ArrowRight size={14} weight="bold" /></Link>
            <Link to="/contact" className="btn-secondary">Talk to a consultant</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-gos-border p-4 rounded-sm">
      <Icon size={18} weight="duotone" className="text-gos-sand" />
      <div className="overline mt-2 !text-gos-muted">{label}</div>
      <div className="font-display text-xl text-gos-navy mt-1">{value}</div>
    </div>
  );
}

function capitalize(s) { return s.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" "); }
